/**
 * Best-effort sanitization for artist story HTML (contentEditable / API).
 * Removes dangerous tags and inline event handlers before dangerouslySetInnerHTML.
 */

const FORBIDDEN_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'meta',
  'link',
  'base',
]);

function stripUnsafeWithoutDom(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/\s+on\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+on\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\s+on\w+\s*=\s*[^\s>]+/gi, '');
}

export function sanitizeStoryHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';

  if (typeof document === 'undefined') {
    return stripUnsafeWithoutDom(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const toRemove: Element[] = [];
  doc.body.querySelectorAll('*').forEach((el) => {
    const tag = el.tagName.toLowerCase();
    if (FORBIDDEN_TAGS.has(tag)) {
      toRemove.push(el);
    }
  });
  toRemove.forEach((el) => el.remove());

  doc.body.querySelectorAll('*').forEach((el) => {
    const tag = el.tagName.toLowerCase();
    [...el.attributes].forEach((attr) => {
      const n = attr.name.toLowerCase();
      if (n.startsWith('on') || n === 'srcdoc') {
        el.removeAttribute(attr.name);
        return;
      }
      if (tag === 'a' && n === 'href' && /^\s*javascript:/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

/** Plain text for summaries / legacy consumers (no HTML entities decoded beyond textContent). */
export function htmlToPlainText(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';

  if (typeof document === 'undefined') {
    return trimmed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}
